import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, Plus, Minus, ChefHat, Coffee, UtensilsCrossed, AlertCircle, Soup, GlassWater, IceCream } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';

/**
 * FormulaSelectionModal - Pop-up intelligent pour la sélection des éléments d'une formule
 * 
 * Structure des steps attendue:
 * {
 *   name: "Plat",
 *   required: true,
 *   min_choices: 1,
 *   max_choices: 1,
 *   choices: [
 *     { product_id, product_name, price_adjustment, destination, product_category }
 *   ]
 * }
 */
const FormulaSelectionModal = ({
    isOpen,
    onClose,
    formula,
    products = [],
    supplements = [],
    onAddToCart,
}) => {
    // State for selections
    const [selections, setSelections] = useState({});
    const [selectedSupplements, setSelectedSupplements] = useState([]);

    // Icons mapping for step types
    const stepIcons = {
        'plat': Soup,
        'boisson': GlassWater,
        'dessert': IceCream,
        'default': UtensilsCrossed
    };

    const getStepIcon = (stepName) => {
        const name = stepName.toLowerCase();
        if (name.includes('plat')) return Soup;
        if (name.includes('boisson')) return GlassWater;
        if (name.includes('dessert') || name.includes('fruit')) return IceCream;
        return UtensilsCrossed;
    };

    // Reset selections when formula changes
    useEffect(() => {
        if (formula && isOpen) {
            const initialSelections = {};
            (formula.steps || []).forEach((step, idx) => {
                const maxChoices = step.max_choices || 1;
                // Pre-select default choice if exists
                const defaultChoice = step.choices?.find(c => c.is_default);
                if (defaultChoice) {
                    initialSelections[idx] = maxChoices > 1 ? [defaultChoice.product_id] : defaultChoice.product_id;
                } else {
                    initialSelections[idx] = maxChoices > 1 ? [] : null;
                }
            });
            setSelections(initialSelections);
            setSelectedSupplements([]);
        }
    }, [formula, isOpen]);

    // Check if all required steps are completed
    const allRequiredCompleted = useMemo(() => {
        if (!formula?.steps) return true;
        
        return formula.steps.every((step, idx) => {
            const isRequired = step.required !== false;
            if (!isRequired) return true;
            
            const selection = selections[idx];
            const minChoices = step.min_choices || 1;
            const maxChoices = step.max_choices || 1;
            
            if (maxChoices > 1) {
                return Array.isArray(selection) && selection.length >= minChoices;
            }
            return selection !== null && selection !== undefined;
        });
    }, [formula, selections]);

    // Calculate total price with adjustments
    const totalPrice = useMemo(() => {
        let total = formula?.price || 0;
        
        // Add price adjustments from selected items
        (formula?.steps || []).forEach((step, idx) => {
            const selection = selections[idx];
            const maxChoices = step.max_choices || 1;
            
            if (maxChoices > 1 && Array.isArray(selection)) {
                selection.forEach(prodId => {
                    const choice = step.choices?.find(c => c.product_id === prodId);
                    if (choice?.price_adjustment) {
                        total += choice.price_adjustment;
                    }
                });
            } else if (selection) {
                const choice = step.choices?.find(c => c.product_id === selection);
                if (choice?.price_adjustment) {
                    total += choice.price_adjustment;
                }
            }
        });
        
        // Add supplements
        selectedSupplements.forEach(sup => {
            total += sup.price || 0;
        });
        
        return total;
    }, [formula, selections, selectedSupplements]);

    // Get selected items for display and kitchen routing
    const getSelectedItems = () => {
        const items = [];
        (formula?.steps || []).forEach((step, idx) => {
            const selection = selections[idx];
            const maxChoices = step.max_choices || 1;
            
            if (maxChoices > 1 && Array.isArray(selection)) {
                selection.forEach(prodId => {
                    const choice = step.choices?.find(c => c.product_id === prodId);
                    if (choice) {
                        items.push({
                            step_name: step.name,
                            product_id: prodId,
                            product_name: choice.product_name,
                            price_adjustment: choice.price_adjustment || 0,
                            destination: choice.destination || 'cuisine',
                            category: choice.product_category || step.name.toLowerCase()
                        });
                    }
                });
            } else if (selection) {
                const choice = step.choices?.find(c => c.product_id === selection);
                if (choice) {
                    items.push({
                        step_name: step.name,
                        product_id: selection,
                        product_name: choice.product_name,
                        price_adjustment: choice.price_adjustment || 0,
                        destination: choice.destination || 'cuisine',
                        category: choice.product_category || step.name.toLowerCase()
                    });
                }
            }
        });
        return items;
    };

    // Handle selection for a step
    const handleSelect = (stepIdx, productId, maxChoices = 1) => {
        setSelections(prev => {
            const newSelections = { ...prev };
            if (maxChoices > 1) {
                // Multiple selection
                const current = Array.isArray(prev[stepIdx]) ? prev[stepIdx] : [];
                if (current.includes(productId)) {
                    newSelections[stepIdx] = current.filter(id => id !== productId);
                } else if (current.length < maxChoices) {
                    newSelections[stepIdx] = [...current, productId];
                }
            } else {
                // Single selection (toggle off if already selected)
                newSelections[stepIdx] = prev[stepIdx] === productId ? null : productId;
            }
            return newSelections;
        });
    };

    // Handle supplement selection
    const handleSupplementToggle = (supplement) => {
        setSelectedSupplements(prev => {
            const exists = prev.find(s => s.id === supplement.id);
            if (exists) {
                return prev.filter(s => s.id !== supplement.id);
            }
            return [...prev, supplement];
        });
    };

    // Handle add to cart
    const handleAddToCart = () => {
        const selectedItems = getSelectedItems();
        
        // Build kitchen items grouped by destination
        const kitchenItems = selectedItems.map(item => ({
            name: item.product_name,
            product_id: item.product_id,
            destination: item.destination,
            category: item.category,
            quantity: 1,
            is_formula_item: true,
            formula_name: formula.name
        }));
        
        // Add supplements as separate kitchen items
        selectedSupplements.forEach(sup => {
            kitchenItems.push({
                name: sup.name,
                product_id: sup.id,
                destination: sup.destination || 'cuisine',
                category: 'supplement',
                quantity: 1,
                is_supplement: true,
                formula_name: formula.name
            });
        });

        const formulaOrder = {
            id: `formula-${formula.id}-${Date.now()}`,
            product_id: formula.id,
            product_name: formula.name,
            name: formula.name,
            price: totalPrice,
            base_price: formula.price,
            quantity: 1,
            is_formula: true,
            meal_type: formula.meal_type,
            selected_items: selectedItems,
            supplements: selectedSupplements.map(s => ({
                id: s.id,
                name: s.name,
                price: s.price
            })),
            // For kitchen/bar display - items decomposed by destination
            kitchen_items: kitchenItems.filter(i => i.destination === 'cuisine' || i.destination === 'boissons_chaudes'),
            bar_items: kitchenItems.filter(i => i.destination === 'bar'),
            all_items: kitchenItems
        };
        
        onAddToCart(formulaOrder);
        onClose();
    };

    if (!isOpen || !formula) return null;

    const steps = formula.steps || [];
    const hasSupplements = supplements.length > 0 || (formula.available_supplements || []).length > 0;
    const availableSupplements = formula.available_supplements || supplements;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="formula-selection-modal">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700 bg-gradient-to-r from-emerald-500 to-emerald-600">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <UtensilsCrossed className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">{formula.name}</h2>
                            {formula.description && (
                                <p className="text-sm text-white/80 line-clamp-1">{formula.description}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge className="bg-white text-emerald-600 font-bold text-lg px-3 py-1 shadow-sm">
                            {totalPrice.toLocaleString()} F
                        </Badge>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                            data-testid="close-formula-modal"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <ScrollArea className="flex-1 p-4">
                    <div className="space-y-5">
                        {/* Steps */}
                        {steps.map((step, stepIdx) => {
                            const isRequired = step.required !== false;
                            const maxChoices = step.max_choices || 1;
                            const minChoices = step.min_choices || (isRequired ? 1 : 0);
                            const currentSelection = selections[stepIdx];
                            const selectionCount = maxChoices > 1 
                                ? (Array.isArray(currentSelection) ? currentSelection.length : 0)
                                : (currentSelection ? 1 : 0);
                            const isComplete = selectionCount >= minChoices;
                            const StepIcon = getStepIcon(step.name);

                            return (
                                <div key={stepIdx} className="space-y-2.5" data-testid={`formula-step-${stepIdx}`}>
                                    {/* Step Header */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className={cn(
                                                "w-7 h-7 rounded-full flex items-center justify-center",
                                                isComplete 
                                                    ? "bg-emerald-500 text-white" 
                                                    : "bg-gray-100 dark:bg-zinc-700 text-gray-500 dark:text-gray-400"
                                            )}>
                                                {isComplete ? <Check className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                                            </div>
                                            <h3 className="font-bold text-gray-900 dark:text-white">
                                                {step.name}
                                            </h3>
                                            {isRequired && !isComplete && (
                                                <Badge variant="outline" className="text-[10px] border-red-300 text-red-600 bg-red-50 dark:bg-red-900/20">
                                                    Obligatoire
                                                </Badge>
                                            )}
                                            {isRequired && isComplete && (
                                                <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20">
                                                    ✓
                                                </Badge>
                                            )}
                                        </div>
                                        {maxChoices > 1 && (
                                            <Badge variant="secondary" className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                                {selectionCount}/{maxChoices} choix
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Choices Grid - 2 columns mobile, 3 on larger screens */}
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {(step.choices || []).slice(0, 6).map((choice) => {
                                            const isSelected = maxChoices > 1
                                                ? (Array.isArray(currentSelection) && currentSelection.includes(choice.product_id))
                                                : currentSelection === choice.product_id;
                                            const hasPriceAdjustment = choice.price_adjustment && choice.price_adjustment > 0;

                                            return (
                                                <button
                                                    key={choice.product_id}
                                                    onClick={() => handleSelect(stepIdx, choice.product_id, maxChoices)}
                                                    data-testid={`choice-${choice.product_id}`}
                                                    className={cn(
                                                        "relative p-2.5 rounded-xl border-2 text-left transition-all",
                                                        "hover:shadow-md active:scale-[0.97] touch-manipulation",
                                                        isSelected
                                                            ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-sm"
                                                            : "border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-gray-300"
                                                    )}
                                                >
                                                    {isSelected && (
                                                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                                                            <Check className="w-3 h-3 text-white" />
                                                        </div>
                                                    )}
                                                    <p className={cn(
                                                        "font-medium text-sm leading-tight pr-5",
                                                        isSelected ? "text-emerald-700 dark:text-emerald-400" : "text-gray-900 dark:text-white"
                                                    )}>
                                                        {choice.product_name}
                                                    </p>
                                                    {hasPriceAdjustment && (
                                                        <p className="text-xs text-orange-600 font-semibold mt-1">
                                                            +{choice.price_adjustment.toLocaleString()} F
                                                        </p>
                                                    )}
                                                    {!hasPriceAdjustment && (
                                                        <p className="text-[10px] text-gray-400 mt-1">Inclus</p>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    {/* Show more if > 6 choices */}
                                    {(step.choices || []).length > 6 && (
                                        <p className="text-xs text-gray-500 text-center">
                                            + {(step.choices || []).length - 6} autres options disponibles
                                        </p>
                                    )}
                                </div>
                            );
                        })}

                        {/* Supplements Section */}
                        {hasSupplements && availableSupplements.length > 0 && (
                            <>
                                <Separator className="my-4" />
                                <div className="space-y-2.5" data-testid="supplements-section">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                            <Plus className="w-4 h-4 text-orange-600" />
                                        </div>
                                        <h3 className="font-bold text-gray-900 dark:text-white">
                                            Suppléments
                                        </h3>
                                        <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-500">
                                            Optionnel
                                        </Badge>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {availableSupplements.map((supplement) => {
                                            const isSelected = selectedSupplements.some(s => s.id === supplement.id);

                                            return (
                                                <button
                                                    key={supplement.id}
                                                    onClick={() => handleSupplementToggle(supplement)}
                                                    data-testid={`supplement-${supplement.id}`}
                                                    className={cn(
                                                        "relative p-2.5 rounded-xl border-2 text-left transition-all",
                                                        "hover:shadow-md active:scale-[0.97] touch-manipulation",
                                                        isSelected
                                                            ? "border-orange-500 bg-orange-50 dark:bg-orange-900/30 shadow-sm"
                                                            : "border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-gray-300"
                                                    )}
                                                >
                                                    {isSelected && (
                                                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center shadow-sm">
                                                            <Check className="w-3 h-3 text-white" />
                                                        </div>
                                                    )}
                                                    <p className={cn(
                                                        "font-medium text-sm leading-tight pr-5",
                                                        isSelected ? "text-orange-700 dark:text-orange-400" : "text-gray-900 dark:text-white"
                                                    )}>
                                                        {supplement.name}
                                                    </p>
                                                    <p className="text-xs text-orange-600 font-semibold mt-1">
                                                        +{supplement.price?.toLocaleString()} F
                                                    </p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </ScrollArea>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/80">
                    {/* Summary of selection */}
                    {getSelectedItems().length > 0 && (
                        <div className="mb-3 p-2.5 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-700">
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1.5 font-medium uppercase tracking-wide">Votre sélection</p>
                            <div className="flex flex-wrap gap-1.5">
                                {getSelectedItems().map((item, idx) => (
                                    <Badge 
                                        key={idx} 
                                        variant="secondary" 
                                        className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400"
                                    >
                                        {item.product_name}
                                        {item.price_adjustment > 0 && (
                                            <span className="ml-1 text-orange-600">+{item.price_adjustment.toLocaleString()}F</span>
                                        )}
                                    </Badge>
                                ))}
                                {selectedSupplements.map((sup, idx) => (
                                    <Badge 
                                        key={`sup-${idx}`} 
                                        variant="secondary" 
                                        className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400"
                                    >
                                        + {sup.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Warning if not complete */}
                    {!allRequiredCompleted && (
                        <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                Veuillez compléter tous les choix obligatoires
                            </p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="flex-1 h-12"
                            data-testid="cancel-formula-btn"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleAddToCart}
                            disabled={!allRequiredCompleted}
                            data-testid="add-formula-to-cart-btn"
                            className={cn(
                                "flex-1 h-12 font-bold text-base",
                                allRequiredCompleted
                                    ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                            )}
                        >
                            <Check className="w-5 h-5 mr-2" />
                            Ajouter • {totalPrice.toLocaleString()} F
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormulaSelectionModal;
